import { Typography, message } from 'antd';
import { GlobalOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { useBusinessStore } from '../../store/businessStore';
import { useAuthStore } from '../../store/authStore';
import { ownerMeApi } from '../../api/ownerMeApi';
import { LANGUAGE_OPTIONS } from '../../i18n/languageOptions';
import { telegram } from '../../utils/telegram';
import type { LanguageCode } from '../../types/i18n';

const VALID_CODES: LanguageCode[] = ['uz', 'ru', 'en'];

/**
 * Platform interface language switcher for the Owner.
 *
 * Applies the language locally (i18next + localStorage + Zustand) and persists
 * the choice to the backend via PATCH /v1/owner/me/language — mirrors the
 * provider's switcher so the preference survives across sessions and devices.
 */
export default function InterfaceLanguageCard() {
  const { t } = useTranslation(['common', 'owner']);
  const { selectedBusiness } = useBusinessStore();
  const { currentLanguage, setLanguage } = useLanguageStore();
  const setUser = useAuthStore((s) => s.setUser);

  if (!selectedBusiness) return null;

  const supportedLanguages = selectedBusiness.supportedLanguages.filter(
    (l): l is LanguageCode => VALID_CODES.includes(l as LanguageCode),
  );

  if (supportedLanguages.length <= 1) return null;

  const options = LANGUAGE_OPTIONS.filter((opt) => supportedLanguages.includes(opt.value));

  const handleChange = (lang: LanguageCode) => {
    if (lang === currentLanguage) return;
    const previous = currentLanguage;

    setLanguage(lang);
    telegram.hapticFeedback.impact('light');

    void ownerMeApi
      .updateLanguage(lang)
      .then((res) => {
        setUser({ preferredLanguage: res.preferredLanguage });
      })
      .catch((err: unknown) => {
        const error = err as { response?: { data?: { code?: string } } };
        const code = error?.response?.data?.code;
        // Faqat backend tilni rad etganda (validatsiya xatosi) UI eski holatga
        // qaytariladi. 404 (endpoint hali yo'q) / 5xx / tarmoq xatosida lokal
        // tanlov saqlanadi — til i18next + localStorage'da allaqachon qo'llangan.
        if (code === 'INVALID_LANGUAGE_CODE' || code === 'LANGUAGE_NOT_SUPPORTED') {
          setLanguage(previous);
          telegram.hapticFeedback.notification('error');
          void message.error(t('owner:settings.interfaceLanguage.errorSaving'));
        }
      });
  };

  return (
    <div style={{
      borderRadius: 16,
      background: 'var(--bl-surface, #fff)',
      border: '1px solid var(--bl-divider, #ebebeb)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 14px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        borderBottom: '1px solid var(--bl-divider, #ebebeb)',
      }}>
        <div style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          background: 'var(--bl-accent-soft, #e7f1fe)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          color: 'var(--bl-accent, #3390ec)',
          flexShrink: 0,
        }}>
          <GlobalOutlined />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <Typography.Text style={{ fontSize: 15, fontWeight: 700, color: 'var(--bl-text-strong, #0f172a)' }}>
            {t('owner:settings.interfaceLanguage.title')}
          </Typography.Text>
          <Typography.Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #6b7280)' }}>
            {t('owner:settings.interfaceLanguage.subtitle')}
          </Typography.Text>
        </div>
      </div>

      {/* Selectable language rows */}
      <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {options.map((opt) => {
          const active = opt.value === currentLanguage;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleChange(opt.value)}
              aria-pressed={active}
              aria-label={opt.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                padding: '10px 12px',
                borderRadius: 12,
                cursor: 'pointer',
                textAlign: 'left',
                background: active ? 'var(--bl-accent-soft, #e7f1fe)' : 'transparent',
                border: active
                  ? '1px solid var(--bl-accent, #3390ec)'
                  : '1px solid var(--bl-divider, #ebebeb)',
                transition: 'background 0.15s ease, border-color 0.15s ease',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <span style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                lineHeight: 1,
                background: 'var(--bl-surface-soft, rgba(16,24,40,0.04))',
                flexShrink: 0,
              }}>
                {opt.flag}
              </span>
              <span style={{
                flex: 1,
                fontSize: 15,
                fontWeight: active ? 700 : 500,
                color: active ? 'var(--bl-accent, #3390ec)' : 'var(--bl-text-strong, #0f172a)',
              }}>
                {opt.label}
              </span>
              {active && (
                <CheckCircleFilled style={{ fontSize: 18, color: 'var(--bl-accent, #3390ec)', flexShrink: 0 }} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
